import React, {
  Suspense,
  useMemo,
  useReducer,
  useEffect,
  useCallback,
  useRef,
  useState,
} from "react";
import type { FC, Dispatch, RefObject } from "react";

import {
  Box,
  Text,
  VStack,
  Button,
  useToast,
  HStack,
  CloseIcon,
  Pressable,
  useDisclose,
  Center,
} from "native-base";
import { useQueryLoader, usePreloadedQuery, useMutation } from "react-relay";
import type { PreloadedQuery } from "react-relay";
import type { Subscription, RecordSourceSelectorProxy } from "relay-runtime";

import SideBar from "pianofunclub-crm/components/Drawers/SideBar";
import ItemStatus from "pianofunclub-crm/components/General/ItemStatus";
import { LOAD_X_BLACKLISTED_DATES } from "pianofunclub-crm/components/Schools/BlacklistedDates";
import SchoolDates from "pianofunclub-crm/components/Tabs/SchoolDates";
import type { SchoolDatesReducerValues } from "pianofunclub-crm/components/Tabs/SchoolDates";
import SchoolLessons, {
  LOAD_X_LESSON_BLOCKS,
} from "pianofunclub-crm/components/Tabs/SchoolLessons";
import SchoolRates from "pianofunclub-crm/components/Tabs/SchoolRates";
import type {
  AccountsStackNavigatorProps,
  AccountsStackRouteProps,
} from "pianofunclub-crm/navigation/AccountsNavigator";
import type {
  InvoicingStackNavigatorProps,
  InvoicingStackRouteProps,
} from "pianofunclub-crm/navigation/InvoicingNavigator";
import type {
  PayrollStackNavigatorProps,
  PayrollStackRouteProps,
} from "pianofunclub-crm/navigation/PayrollNavigator";
import type {
  RegistersStackNavigatorProps,
  RegistersStackRouteProps,
} from "pianofunclub-crm/navigation/RegistersNavigator";
import type {
  SchoolsStackNavigatorProps,
  SchoolsStackRouteProps,
} from "pianofunclub-crm/navigation/SchoolsNavigator";
import type {
  WaitingListStackNavigatorProps,
  WaitingListStackRouteProps,
} from "pianofunclub-crm/navigation/WaitingListNavigator";

import LoadingBlobs from "pianofunclub-shared/components/Animations/LoadingBlobs";
import ProblemSplash from "pianofunclub-shared/components/Base/ProblemSplash";
import ButtonDebounced from "pianofunclub-shared/components/Buttons/ButtonDebounced";
import TopTabBar from "pianofunclub-shared/components/Buttons/TopTabBar";
import TextInput from "pianofunclub-shared/components/Inputs/TextInput";
import type { TextInputRef } from "pianofunclub-shared/components/Inputs/TextInput";
import ListEmptyBanner from "pianofunclub-shared/components/ListItems/ListEmptyBanner";
import type { UpdateBlockDetailsModalInfo } from "pianofunclub-shared/components/Modals/UpdateLessonBlockDetailsModal";
import Actionsheet from "pianofunclub-shared/components/NativeBaseExtended/Actionsheet";
import AlertPopup from "pianofunclub-shared/components/NativeBaseExtended/AlertPopup";
import Select from "pianofunclub-shared/components/NativeBaseExtended/Select";
import ToastAlert from "pianofunclub-shared/components/NativeBaseExtended/ToastAlert";
import {
  AddCircleIcon,
  ArchivedIcon,
  TickIcon,
  TrashIcon,
  WebsiteIcon,
} from "pianofunclub-shared/components/Other/Icons";
import { useAuth } from "pianofunclub-shared/providers/AuthProviderContext";
import type { LoadLessonBlocksQuery } from "pianofunclub-shared/relay/graphql/registers/__generated__/LoadLessonBlocksQuery.graphql";
import { load_lesson_blocks } from "pianofunclub-shared/relay/graphql/registers/LoadLessonBlocks";
import type {
  EditSchoolMutation,
  EditSchoolMutation$data,
} from "pianofunclub-shared/relay/graphql/schools/__generated__/EditSchoolMutation.graphql";
import type { LoadBlacklistedDatesQuery } from "pianofunclub-shared/relay/graphql/schools/__generated__/LoadBlacklistedDatesQuery.graphql";
import type { LoadSchoolQuery } from "pianofunclub-shared/relay/graphql/schools/__generated__/LoadSchoolQuery.graphql";
import type { LoadTermDatesQuery } from "pianofunclub-shared/relay/graphql/schools/__generated__/LoadTermDatesQuery.graphql";
import { edit_school } from "pianofunclub-shared/relay/graphql/schools/EditSchool";
import { load_blacklisted_dates } from "pianofunclub-shared/relay/graphql/schools/LoadBlacklistedDates";
import { load_school } from "pianofunclub-shared/relay/graphql/schools/LoadSchool";
import { load_term_dates } from "pianofunclub-shared/relay/graphql/schools/LoadTermDates";
import { SCHOOL_TYPES } from "pianofunclub-shared/utils/constants";
import { titleCaseConverter } from "pianofunclub-shared/utils/converters";
import {
  getCurrentBlock,
  getDefaultStartingYearAndStartingYearsForSelect,
} from "pianofunclub-shared/utils/extractors";
import { createReducer } from "pianofunclub-shared/utils/reducers";
import type { State, Action } from "pianofunclub-shared/utils/reducers";

export type NavigationProps = SchoolsStackNavigatorProps<"School"> &
  AccountsStackNavigatorProps<"School"> &
  RegistersStackNavigatorProps<"School"> &
  InvoicingStackNavigatorProps<"School"> &
  WaitingListStackNavigatorProps<"School"> &
  PayrollStackNavigatorProps<"School">;

type RouteProps = SchoolsStackRouteProps<"School"> &
  AccountsStackRouteProps<"School"> &
  RegistersStackRouteProps<"School"> &
  InvoicingStackRouteProps<"School"> &
  WaitingListStackRouteProps<"School"> &
  PayrollStackRouteProps<"School">;

interface ScreenProps {
  navigation: NavigationProps;
  route: RouteProps;
}

interface ContentProps {
  dispatchState: Dispatch<Action<ReducerValues>>;
  loadSchoolQueryReference: PreloadedQuery<LoadSchoolQuery, Record<string, unknown>>;
  navigation: NavigationProps;
  route: RouteProps;
  schoolId: string;
  startingYears: {
    label: string;
    value: number;
  }[];
  state: State<ReducerValues>;
}

export interface ReducerValues extends SchoolDatesReducerValues {
  addLessonBlockModalIsOpen?: boolean;
  block?: number;
  contentIsRendered?: boolean;
  createLessonStageModalIsOpen?: boolean;
  currentPageIndex?: number;
  dayIndex?: number;
  deleteSchoolAlertIsOpen?: boolean;
  isRefetching?: boolean;
  lessonBlocksConnectionId?: string;
  orderBy?: string;
  schoolTypeSelect?: string;
  searchTerm?: string;
  sideBarIsCollapsed?: boolean;
  statusChangeInFlight?: boolean;
  subscription?: Subscription;
  termDatePickerDate?: Date;
  termDatePickerInputRef?: RefObject<{
    setValue: (text: string, isValid: boolean) => void;
  }>;
  updateBlockDetailsModalInfo?: UpdateBlockDetailsModalInfo;
}

const SIDE_BAR_EXPANDED_WIDTH = 400;
const SIDE_BAR_COLLAPSED_WIDTH = 12;

const SchoolContent: FC<ContentProps> = (props) => {
  const {
    dispatchState,
    loadSchoolQueryReference,
    navigation,
    route,
    schoolId,
    startingYears,
    state,
  } = props;

  const schoolData = usePreloadedQuery(load_school, loadSchoolQueryReference);

  const [loadLessonBlocksQueryReference, loadLessonBlocksQuery] =
    useQueryLoader<LoadLessonBlocksQuery>(load_lesson_blocks);

  useEffect(() => {
    if (
      schoolData.school &&
      (!state.values.contentIsRendered || state.values.currentPageIndex == 2)
    ) {
      loadLessonBlocksQuery(
        {
          block: state.values.block,
          dayIndex: state.values.dayIndex,
          first: LOAD_X_LESSON_BLOCKS,
          orderBy: state.values.orderBy,
          profileId: "",
          schoolId,
          searchTerm: state.values.searchTerm?.trim(),
          skipLessonBlocks: false,
          skipLessonStages: false,
          skipRegisterProgress: true,
          startingYear: state.values.startingYear,
        },
        { fetchPolicy: "store-or-network" }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loadLessonBlocksQuery,
    schoolData.school,
    state.values.block,
    state.values.dayIndex,
    state.values.orderBy,
    state.values.searchTerm,
    state.values.startingYear,
  ]);

  useEffect(() => {
    dispatchState({
      input: "schoolTypeSelect",
      value: schoolData.school?.schoolType ?? "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolData.school?.schoolType]);

  const refreshHandler = useCallback(
    (onRefresh?: () => void) => {
      onRefresh?.();

      loadLessonBlocksQuery(
        {
          block: state.values.block,
          dayIndex: state.values.dayIndex,
          first: LOAD_X_LESSON_BLOCKS,
          orderBy: state.values.orderBy,
          profileId: "",
          schoolId,
          searchTerm: state.values.searchTerm?.trim(),
          skipLessonBlocks: false,
          skipLessonStages: false,
          skipRegisterProgress: true,
          startingYear: state.values.startingYear,
        },
        { fetchPolicy: "network-only" }
      );
    },
    [
      loadLessonBlocksQuery,
      schoolId,
      state.values.block,
      state.values.dayIndex,
      state.values.orderBy,
      state.values.searchTerm,
      state.values.startingYear,
    ]
  );

  const [loadTermDatesQueryReference, loadTermDatesQuery] =
    useQueryLoader<LoadTermDatesQuery>(load_term_dates);

  const [loadBlacklistedDatesQueryReference, loadBlacklistedDatesQuery] =
    useQueryLoader<LoadBlacklistedDatesQuery>(load_blacklisted_dates);

  useEffect(() => {
    loadTermDatesQuery(
      {
        schoolId,
        skipSchoolTermDates: false,
        startingYear: state.values.startingYear,
      },
      { fetchPolicy: "store-or-network" }
    );

    loadBlacklistedDatesQuery({
      first: LOAD_X_BLACKLISTED_DATES,
      schoolId,
      startingYear: state.values.startingYear,
    });
  }, [loadBlacklistedDatesQuery, loadTermDatesQuery, schoolId, state.values.startingYear]);

  const termDatesAndBlacklistedDatesRefreshHandler = useCallback(() => {
    loadTermDatesQuery(
      {
        schoolId,
        skipSchoolTermDates: false,
        startingYear: state.values.startingYear,
      },
      { fetchPolicy: "network-only" }
    );

    loadBlacklistedDatesQuery(
      {
        first: LOAD_X_BLACKLISTED_DATES,
        schoolId,
        startingYear: state.values.startingYear,
      },
      { fetchPolicy: "network-only" }
    );
  }, [loadBlacklistedDatesQuery, loadTermDatesQuery, schoolId, state.values.startingYear]);

  const [otherContacts, setOtherContacts] = useState(
    schoolData.school?.otherContacts.edges.map((item, index) => {
      if (item?.node) {
        return {
          contactIdentifier: `otherContact${index + 1}`,
          contactLabel: item.node.label,
          hasInvalidPhoneNumber: item.node.hasInvalidPhoneNumber,
          initialEmail: item.node.user?.email,
          initialFirstName: item.node.user?.firstName,
          initialLastName: item.node.user?.lastName,
          initialPhoneNumber: item.node.phoneNumber,
        };
      }
    }) ?? []
  );

  useEffect(() => {
    setOtherContacts(
      schoolData.school?.otherContacts.edges.map((item, index) => {
        if (item?.node) {
          return {
            contactIdentifier: `otherContact${index + 1}`,
            contactLabel: item.node.label,
            hasInvalidPhoneNumber: item.node.hasInvalidPhoneNumber,
            initialEmail: item.node.user?.email,
            initialFirstName: item.node.user?.firstName,
            initialLastName: item.node.user?.lastName,
            initialPhoneNumber: item.node.phoneNumber,
          };
        }
      }) ?? []
    );
  }, [schoolData.school?.otherContacts.edges]);

  const toast = useToast();
  const addressSecondLineInputRef = useRef<TextInputRef>(null);
  const cityInputRef = useRef<TextInputRef>(null);
  const postcodeInputRef = useRef<TextInputRef>(null);
  const {
    isOpen: changeStatusActionsheetIsOpen,
    onClose: changeStatusActionsheetOnClose,
    onOpen: changeStatusActionsheetOnOpen,
  } = useDisclose();

  const [commitEditSchool, editSchoolInFlight] = useMutation<EditSchoolMutation>(edit_school);

  const editSchool = useCallback(
    (
      variables: {
        addressFirstLine?: string;
        addressSecondLine?: string;
        archived?: boolean;
        city?: string;
        deleteSchool?: boolean;
        headTeacherEmail?: string;
        headTeacherFirstName?: string;
        headTeacherLastName?: string;
        headTeacherPhoneNumber?: string;
        mainOfficeContactEmail?: string;
        mainOfficeContactFirstName?: string;
        mainOfficeContactLastName?: string;
        mainOfficeContactPhoneNumber?: string;
        name?: string;
        otherContactEmail?: string;
        otherContactFirstName?: string;
        otherContactIndex?: number;
        otherContactLabel?: string;
        otherContactLastName?: string;
        otherContactPhoneNumber?: string;
        postcode?: string;
        schoolId: string;
        website?: string;
      },
      isContactUpdate?: boolean
    ) => {
      const editSchoolConfig = {
        variables: {
          connections: route.params.schoolsConnectionIds ?? [],
          input: variables,
        },
        onCompleted: (response: EditSchoolMutation$data) => {
          if (response?.editSchool?.success) {
            if (variables.deleteSchool) {
              navigation.popToTop();
            }
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  id={id}
                  status={variables.deleteSchool || variables.archived ? "info" : "success"}
                  title={`${isContactUpdate ? "Contact" : "School"} ${
                    variables.deleteSchool ? "deleted" : variables.archived ? "archived" : "updated"
                  }`}
                  toast={toast}
                />
              ),
            });
          } else {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  description={"Please get in touch with one of the team"}
                  id={id}
                  status="error"
                  title={`Couldn't ${
                    variables.deleteSchool ? "delete" : variables.archived ? "archive" : "update"
                  } ${isContactUpdate ? "contact" : "school"}`}
                  toast={toast}
                />
              ),
            });
          }
          dispatchState({
            input: "statusChangeInFlight",
            value: false,
          });
        },
        updater: (store: RecordSourceSelectorProxy) => {
          // invalidate record in store so that queries for register action modals are refetched
          // (archived teachers are removed from those modals)
          if (variables.archived !== undefined || variables.deleteSchool) {
            const schoolRecord = store.get(variables.schoolId);
            if (schoolRecord) {
              schoolRecord.invalidateRecord();
            }
          }
        },
      };

      commitEditSchool(editSchoolConfig);
    },
    [commitEditSchool, dispatchState, navigation, route.params.schoolsConnectionIds, toast]
  );

  const schoolInputFinishEditingHandler = useCallback(
    (inputIdentifier?: string | number, inputValue?: string, isValid?: boolean) => {
      if (isValid && inputIdentifier) {
        if (
          // check if the value has changed before firing mutation
          // @ts-expect-error string indexing
          inputValue !== (schoolData.school?.[inputIdentifier] ?? "")
        ) {
          editSchool({
            [inputIdentifier]: inputValue?.trim(),
            schoolId,
          });
        }
      }
    },
    [editSchool, schoolData.school, schoolId]
  );

  const selectChangeHandler = useCallback(
    (inputIdentifier: string, itemValue: string | boolean) => {
      if (state.values?.[`${inputIdentifier}Select` as keyof ReducerValues] != itemValue) {
        dispatchState({
          input: `${inputIdentifier}Select` as keyof ReducerValues,
          value: itemValue,
        });
        editSchool({
          [inputIdentifier]: itemValue,
          schoolId,
        });
      }
    },
    [state.values, dispatchState, editSchool, schoolId]
  );

  const headTeacherInputFinishEditingHandler = useCallback(
    (inputIdentifier?: string | number, inputValue?: string, isValid?: boolean) => {
      if (isValid && typeof inputIdentifier === "string") {
        let fieldName = inputIdentifier.replace("headTeacher", "");
        fieldName = fieldName.charAt(0).toLowerCase() + fieldName.slice(1);
        if (
          // check if the value has changed before firing mutation
          (fieldName === "phoneNumber" &&
            inputValue != (schoolData.school?.headTeacher?.[fieldName] ?? "")) ||
          (fieldName !== "phoneNumber" &&
            inputValue !=
              // @ts-expect-error string indexing
              (schoolData.school?.headTeacher?.user?.[fieldName] ?? ""))
        ) {
          editSchool(
            {
              [inputIdentifier]: inputValue?.trim(),
              schoolId: schoolId,
            },
            true
          );
        }
      }
    },
    [editSchool, schoolData.school, schoolId]
  );

  const mainOfficeContactInputFinishEditingHandler = useCallback(
    (inputIdentifier?: string | number, inputValue?: string, isValid?: boolean) => {
      if (isValid && typeof inputIdentifier === "string") {
        let fieldName = inputIdentifier.replace("mainOfficeContact", "");
        fieldName = fieldName.charAt(0).toLowerCase() + fieldName.slice(1);
        if (
          // check if the value has changed before firing mutation
          (["phoneNumber", "addressFirstLine", "addressSecondLine", "city", "postcode"].includes(
            fieldName
          ) &&
            inputValue !=
              // @ts-expect-error string indexing
              (schoolData.school?.mainOfficeContact?.[fieldName] ?? "")) ||
          (!["phoneNumber", "addressFirstLine", "addressSecondLine", "city", "postcode"].includes(
            fieldName
          ) &&
            inputValue !=
              // @ts-expect-error string indexing
              (schoolData.school?.mainOfficeContact?.user?.[fieldName] ?? ""))
        ) {
          editSchool(
            {
              [inputIdentifier]: inputValue?.trim(),
              schoolId: schoolId,
            },
            !["addressFirstLine", "addressSecondLine", "city", "postcode"].includes(fieldName)
          );
        }
      }
    },
    [editSchool, schoolData.school, schoolId]
  );

  const otherContactsInputFinishEditingHandler = useCallback(
    (inputIdentifier?: string | number, inputValue?: string, isValid?: boolean) => {
      if (isValid && typeof inputIdentifier === "string") {
        let fieldName = inputIdentifier.replace("otherContact", "").replace(/[0-9]/g, "");
        fieldName = fieldName.charAt(0).toLowerCase() + fieldName.slice(1);
        const contactIndex = inputIdentifier.match(/\d+/g)?.join("");
        if (
          contactIndex &&
          // check if the value has changed before firing mutation
          ((fieldName === "phoneNumber" &&
            inputValue !=
              (schoolData.school?.otherContacts.edges?.[parseInt(contactIndex) - 1]?.node?.[
                fieldName
              ] ?? "")) ||
            (fieldName !== "phoneNumber" &&
              inputValue !=
                // @ts-expect-error string indexing
                (schoolData.school?.otherContacts.edges?.[parseInt(contactIndex) - 1]?.node?.user?.[
                  fieldName
                ] ?? "")))
        ) {
          editSchool(
            {
              [inputIdentifier.replace(/[0-9]/g, "")]: inputValue?.trim(),
              otherContactIndex: parseInt(contactIndex),
              schoolId: schoolId,
            },
            true
          );
        }
      }
    },
    [editSchool, schoolData.school, schoolId]
  );

  const addContactHandler = useCallback(() => {
    const nextIdentifier = otherContacts.slice(-1)[0]?.contactIdentifier?.slice(-1);

    setOtherContacts([
      ...otherContacts,
      {
        contactIdentifier: `otherContact${nextIdentifier ? parseInt(nextIdentifier) + 1 : 1}`,
        contactLabel: "",
        hasInvalidPhoneNumber: false,
        initialEmail: "",
        initialFirstName: "",
        initialLastName: "",
        initialPhoneNumber: "",
      },
    ]);
  }, [otherContacts]);

  const removeContactHandler = useCallback(
    (contactIdentifier: string) => {
      setOtherContacts(
        otherContacts.filter((item) => item?.contactIdentifier !== contactIdentifier)
      );
    },
    [otherContacts]
  );

  const renderContactInputs = useCallback(
    (input: {
      contactIdentifier: string;
      contactLabel: string | null | undefined;
      finishEditingHandler: (
        id: string | number | undefined,
        inputValue?: string,
        isValid?: boolean
      ) => void;
      hasInvalidPhoneNumber: boolean | undefined;
      initialEmail: string | undefined;
      initialFirstName: string | undefined;
      initialLastName: string | undefined;
      initialPhoneNumber: string | null | undefined;
      labelIsEditable?: boolean;
    }) => {
      return (
        <>
          <VStack alignItems="flex-start" width="100%">
            {input.labelIsEditable ? (
              <HStack pr="8" width="100%">
                <TextInput
                  _focus={{
                    bg: "transparent",
                    borderWidth: 0,
                  }}
                  _hover={{
                    bg: "transparent",
                    borderWidth: 0,
                  }}
                  autoCapitalize="words"
                  bg="transparent"
                  borderRadius="0"
                  borderWidth={0}
                  color="surface.700"
                  flex={1}
                  focusOutlineColor="transparent"
                  id={`${input.contactIdentifier}Label`}
                  initialValue={input.contactLabel ?? ""}
                  mb="1"
                  ml="1"
                  mt="2"
                  onFinishEditing={input.finishEditingHandler}
                  p="0"
                  placeholder="Who is this contact?"
                  size="md"
                />
                <Button
                  _hover={{
                    bg: "transparent",
                    opacity: 0.8,
                  }}
                  _pressed={{
                    bg: "transparent",
                    opacity: 0.7,
                  }}
                  leftIcon={<CloseIcon color="surface.700" size="4" />}
                  onPress={() => {
                    removeContactHandler(input.contactIdentifier);
                  }}
                  p="1"
                  variant="ghost"
                />
              </HStack>
            ) : null}
            <TextInput
              autoCapitalize="words"
              id={`${input.contactIdentifier}FirstName`}
              initiallyValid
              initialValue={input.initialFirstName ?? ""}
              invalidIndicator
              label={!input.labelIsEditable ? input.contactLabel : undefined}
              labelStyle={{
                color: "surface.700",
                fontSize: "sm",
              }}
              onFinishEditing={input.finishEditingHandler}
              placeholder="First name"
              size="md"
            />
          </VStack>
          <TextInput
            autoCapitalize="words"
            id={`${input.contactIdentifier}LastName`}
            initiallyValid
            initialValue={input.initialLastName ?? ""}
            invalidIndicator
            onFinishEditing={input.finishEditingHandler}
            placeholder="Last name"
            size="md"
          />
          <TextInput
            autoCapitalize="none"
            email
            id={`${input.contactIdentifier}Email`}
            initiallyValid
            initialValue={input.initialEmail ?? ""}
            invalidIndicator
            keyboardType="email-address"
            onFinishEditing={input.finishEditingHandler}
            placeholder="Email"
            size="md"
          />
          <TextInput
            autoCapitalize="none"
            id={`${input.contactIdentifier}PhoneNumber`}
            initiallyValid={!input.hasInvalidPhoneNumber}
            initialValue={input.initialPhoneNumber ?? ""}
            invalidIndicator
            isInvalid={input.hasInvalidPhoneNumber}
            keyboardType="numeric"
            onFinishEditing={input.finishEditingHandler}
            placeholder="Phone number"
            size="md"
            updateValueOnInitialValueChange
          />
        </>
      );
    },
    [removeContactHandler]
  );

  const renderSideBar = useMemo(() => {
    return (
      <SideBar
        isCollapsed={state.values.sideBarIsCollapsed}
        navigation={navigation}
        setIsCollapsed={() =>
          dispatchState({
            input: "sideBarIsCollapsed",
            value: !state.values.sideBarIsCollapsed,
          })
        }
        width={state.values.sideBarIsCollapsed ? SIDE_BAR_COLLAPSED_WIDTH : SIDE_BAR_EXPANDED_WIDTH}
      >
        <VStack alignItems="center" mt="20" pb="6" space="4" width="100%">
          <VStack alignItems="center">
            <Text fontSize="5xl" textAlign="center">
              {"🏫"}
            </Text>
            <Text fontSize="xl" mt="2" textAlign="center">
              {titleCaseConverter(schoolData.school?.name)}
              {schoolData.school?.website ? (
                <Pressable
                  marginLeft="8px"
                  onPress={() => window.open(String(schoolData.school?.website), "_blank")}
                >
                  <Center
                    bg={{
                      linearGradient: {
                        colors: ["secondary.500", "secondary.600"],
                        end: [0.75, 1],
                        start: [0.25, 0],
                      },
                    }}
                    borderRadius="xl"
                    p="5px"
                  >
                    <WebsiteIcon color="surface.100" size="22px" />
                  </Center>
                </Pressable>
              ) : null}
            </Text>
          </VStack>
          <Pressable alignItems="center" mt="2" onPress={() => changeStatusActionsheetOnOpen()}>
            <ItemStatus
              isLoading={editSchoolInFlight && state.values.statusChangeInFlight}
              showEditArrow
              status={schoolData.school?.archived ? "ARCHIVED" : "ACTIVE"}
            />
          </Pressable>
          <VStack alignItems="center" space="2" width="100%">
            <TextInput
              autoCapitalize="words"
              id="name"
              initiallyValid={Boolean(schoolData.school?.name)}
              initialValue={titleCaseConverter(schoolData.school?.name ?? "")}
              invalidIndicator
              label="School name"
              labelStyle={{
                color: "surface.700",
                fontSize: "sm",
              }}
              onFinishEditing={schoolInputFinishEditingHandler}
              size="md"
            />
            <VStack mt="1" space="0.5" width="100%">
              <Text color="surface.700" fontSize="sm" pl="1">
                School type
              </Text>
              <Select
                bg="surface.100"
                borderRadius="full"
                color="surface.900"
                fontSize="sm"
                onValueChange={(itemValue) => selectChangeHandler("schoolType", itemValue)}
                placeholder="Select school type"
                selectedValue={state.values.schoolTypeSelect}
              >
                {SCHOOL_TYPES.map((item) => {
                  return (
                    <Select.Item
                      key={item.value}
                      actionSheetLabel={item.label}
                      value={item.value}
                    />
                  );
                })}
              </Select>
            </VStack>
            <TextInput
              autoCapitalize="words"
              id="website"
              initiallyValid
              initialValue={schoolData.school?.website ?? ""}
              invalidIndicator
              label="Website"
              labelStyle={{
                color: "surface.700",
                fontSize: "sm",
              }}
              onFinishEditing={schoolInputFinishEditingHandler}
              placeholder="Website url"
              size="md"
            />
            <>
              <TextInput
                autoCapitalize="words"
                id="addressFirstLine"
                initiallyValid
                initialValue={schoolData.school?.mainOfficeContact?.addressFirstLine ?? ""}
                invalidIndicator
                label="Address"
                labelStyle={{
                  color: "surface.700",
                  fontSize: "sm",
                }}
                maxLength={200}
                onFinishEditing={mainOfficeContactInputFinishEditingHandler}
                onSubmit={() => addressSecondLineInputRef.current?.focus()}
                placeholder="1st line address"
                size="md"
              />
              <TextInput
                ref={addressSecondLineInputRef}
                autoCapitalize="words"
                id="addressSecondLine"
                initiallyValid
                initialValue={schoolData.school?.mainOfficeContact?.addressSecondLine ?? ""}
                invalidIndicator
                maxLength={200}
                onFinishEditing={mainOfficeContactInputFinishEditingHandler}
                onSubmit={() => cityInputRef.current?.focus()}
                placeholder="2nd line address"
                size="md"
              />
              <TextInput
                ref={cityInputRef}
                autoCapitalize="words"
                id="city"
                initiallyValid
                initialValue={schoolData.school?.mainOfficeContact?.city ?? ""}
                invalidIndicator
                maxLength={50}
                onFinishEditing={mainOfficeContactInputFinishEditingHandler}
                onSubmit={() => postcodeInputRef.current?.focus()}
                placeholder="City or county"
                size="md"
              />
              <TextInput
                ref={postcodeInputRef}
                autoCapitalize="characters"
                id="postcode"
                initiallyValid
                initialValue={schoolData.school?.mainOfficeContact?.postcode ?? ""}
                invalidIndicator
                maxLength={10}
                onFinishEditing={mainOfficeContactInputFinishEditingHandler}
                placeholder="Postcode"
                size="md"
              />
            </>
            {renderContactInputs({
              contactIdentifier: "headTeacher",
              contactLabel: "Head Teacher",
              finishEditingHandler: headTeacherInputFinishEditingHandler,
              hasInvalidPhoneNumber: schoolData.school?.headTeacher?.hasInvalidPhoneNumber,
              initialEmail: schoolData.school?.headTeacher?.user?.email,
              initialFirstName: schoolData.school?.headTeacher?.user?.firstName,
              initialLastName: schoolData.school?.headTeacher?.user?.lastName,
              initialPhoneNumber: schoolData.school?.headTeacher?.phoneNumber,
            })}
            {renderContactInputs({
              contactIdentifier: "mainOfficeContact",
              contactLabel: "School Office",
              finishEditingHandler: mainOfficeContactInputFinishEditingHandler,
              hasInvalidPhoneNumber: schoolData.school?.mainOfficeContact?.hasInvalidPhoneNumber,
              initialEmail: schoolData.school?.mainOfficeContact?.user?.email,
              initialFirstName: schoolData.school?.mainOfficeContact?.user?.firstName,
              initialLastName: schoolData.school?.mainOfficeContact?.user?.lastName,
              initialPhoneNumber: schoolData.school?.mainOfficeContact?.phoneNumber,
            })}
            {otherContacts.map((item) => {
              if (item) {
                return renderContactInputs({
                  contactIdentifier: item.contactIdentifier,
                  contactLabel: item.contactLabel,
                  finishEditingHandler: otherContactsInputFinishEditingHandler,
                  hasInvalidPhoneNumber: item.hasInvalidPhoneNumber,
                  initialEmail: item.initialEmail,
                  initialFirstName: item.initialFirstName,
                  initialLastName: item.initialLastName,
                  initialPhoneNumber: item.initialPhoneNumber,
                  labelIsEditable: true,
                });
              } else {
                return null;
              }
            })}
            <Button
              alignSelf="flex-start"
              leftIcon={<AddCircleIcon size="6" />}
              mt="2"
              onPress={addContactHandler}
              variant="ghost"
            >
              Add Contact
            </Button>
          </VStack>
        </VStack>
      </SideBar>
    );
  }, [
    state.values.sideBarIsCollapsed,
    state.values.statusChangeInFlight,
    state.values.schoolTypeSelect,
    navigation,
    schoolData.school?.name,
    schoolData.school?.website,
    schoolData.school?.archived,
    schoolData.school?.mainOfficeContact?.addressFirstLine,
    schoolData.school?.mainOfficeContact?.addressSecondLine,
    schoolData.school?.mainOfficeContact?.city,
    schoolData.school?.mainOfficeContact?.postcode,
    schoolData.school?.mainOfficeContact?.user?.firstName,
    schoolData.school?.mainOfficeContact?.user?.lastName,
    schoolData.school?.mainOfficeContact?.user?.email,
    schoolData.school?.mainOfficeContact?.phoneNumber,
    schoolData.school?.mainOfficeContact?.hasInvalidPhoneNumber,
    schoolData.school?.headTeacher?.user?.firstName,
    schoolData.school?.headTeacher?.user?.lastName,
    schoolData.school?.headTeacher?.user?.email,
    schoolData.school?.headTeacher?.phoneNumber,
    schoolData.school?.headTeacher?.hasInvalidPhoneNumber,
    editSchoolInFlight,
    schoolInputFinishEditingHandler,
    mainOfficeContactInputFinishEditingHandler,
    renderContactInputs,
    headTeacherInputFinishEditingHandler,
    otherContacts,
    addContactHandler,
    dispatchState,
    changeStatusActionsheetOnOpen,
    selectChangeHandler,
    otherContactsInputFinishEditingHandler,
  ]);

  const safeDeleteAccountRef = useRef(null);

  const renderModals = useMemo(() => {
    return (
      <>
        <Actionsheet
          animationType="fade"
          hideDragIndicator
          isOpen={changeStatusActionsheetIsOpen}
          justifyContent="center"
          onClose={changeStatusActionsheetOnClose}
          size="lg"
        >
          <Actionsheet.Content
            alignItems="center"
            closeButtonProps={{ right: "5", top: "5" }}
            justifyContent="center"
            mx="auto"
            roundedBottom={12}
            roundedTop={12}
            showCloseButton
            w="40%"
          >
            <Text alignSelf="center" fontSize="xl" fontWeight="bold" py="4">
              Update School Status
            </Text>
            <Text
              alignSelf="center"
              fontFamily="Poppins-Regular"
              fontSize="md"
              mx="8"
              pb="2"
              textAlign="center"
            >
              {`${
                !schoolData.school?.archived
                  ? "Archive a school if they are no longer with PianoFunClub.\n\n"
                  : ""
              }Only delete a school if it has been created by mistake.`}
            </Text>
            {!schoolData.school?.archived ? (
              <Actionsheet.Item
                _text={{ fontSize: "lg" }}
                leftIcon={
                  <Center size="7">
                    <ArchivedIcon color="surface.600" size="6" />
                  </Center>
                }
                onPress={() => {
                  dispatchState({
                    input: "statusChangeInFlight",
                    value: true,
                  });
                  editSchool({
                    archived: true,
                    schoolId: schoolId,
                  });
                  changeStatusActionsheetOnClose();
                }}
              >
                Archive school
              </Actionsheet.Item>
            ) : (
              <Actionsheet.Item
                _text={{ fontSize: "lg" }}
                leftIcon={
                  <Center size="7">
                    <TickIcon color="primary.400" size="7" />
                  </Center>
                }
                onPress={() => {
                  dispatchState({
                    input: "statusChangeInFlight",
                    value: true,
                  });
                  editSchool({
                    archived: false,
                    schoolId: schoolId,
                  });
                  changeStatusActionsheetOnClose();
                }}
              >
                Make school active
              </Actionsheet.Item>
            )}
            <Actionsheet.Item
              _text={{ fontSize: "lg" }}
              leftIcon={
                <Center size="7">
                  <TrashIcon color="surface.800" size="6" />
                </Center>
              }
              onPress={() => {
                dispatchState({
                  input: "deleteSchoolAlertIsOpen",
                  value: true,
                });
                changeStatusActionsheetOnClose();
              }}
            >
              Delete school
            </Actionsheet.Item>
          </Actionsheet.Content>
        </Actionsheet>
        <AlertPopup
          alertIsOpen={Boolean(state.values.deleteSchoolAlertIsOpen)}
          body="This cannot be undone and all its data will be permanently lost."
          header={"Are you sure you want to delete this school?"}
          safeButtonRef={safeDeleteAccountRef}
          setAlertIsOpen={(isOpen: boolean) =>
            dispatchState({
              input: "deleteSchoolAlertIsOpen",
              value: isOpen,
            })
          }
        >
          <Button
            ref={safeDeleteAccountRef}
            _text={{ fontSize: "lg" }}
            colorScheme="muted"
            minWidth="100"
            onPress={() => {
              dispatchState({
                input: "deleteSchoolAlertIsOpen",
                value: false,
              });
            }}
            width="40%"
          >
            No, go back
          </Button>
          <ButtonDebounced
            _text={{ fontSize: "lg" }}
            colorScheme="red"
            minWidth="100"
            onPress={() => {
              dispatchState({
                input: "statusChangeInFlight",
                value: true,
              });
              editSchool({
                deleteSchool: true,
                schoolId: schoolId,
              });
              dispatchState({
                input: "deleteSchoolAlertIsOpen",
                value: false,
              });
            }}
            width="40%"
          >
            Yes, delete
          </ButtonDebounced>
        </AlertPopup>
      </>
    );
  }, [
    changeStatusActionsheetIsOpen,
    changeStatusActionsheetOnClose,
    dispatchState,
    editSchool,
    schoolData.school?.archived,
    schoolId,
    state.values.deleteSchoolAlertIsOpen,
  ]);

  const renderTab = useMemo(() => {
    return schoolData.school ? (
      state.values.currentPageIndex == 0 ? (
        <SchoolLessons
          dispatchState={dispatchState}
          loadLessonBlocksQueryReference={loadLessonBlocksQueryReference}
          navigation={navigation}
          refreshHandler={refreshHandler}
          schoolId={schoolData.school.id}
          sideBarWidth={
            state.values.sideBarIsCollapsed ? SIDE_BAR_COLLAPSED_WIDTH : SIDE_BAR_EXPANDED_WIDTH
          }
          startingYears={startingYears}
          state={state}
        />
      ) : state.values.currentPageIndex == 1 ? (
        <SchoolDates
          dispatchScreenState={dispatchState}
          loadBlacklistedDatesQueryReference={loadBlacklistedDatesQueryReference}
          loadTermDatesQueryReference={loadTermDatesQueryReference}
          navigation={navigation}
          refreshHandler={termDatesAndBlacklistedDatesRefreshHandler}
          schoolId={schoolData.school.id}
          screenState={state}
          sideBarWidth={
            state.values.sideBarIsCollapsed ? SIDE_BAR_COLLAPSED_WIDTH : SIDE_BAR_EXPANDED_WIDTH
          }
          startingYears={startingYears}
        />
      ) : state.values.currentPageIndex == 2 ? (
        <SchoolRates
          dispatchState={dispatchState}
          loadLessonBlocksQueryReference={loadLessonBlocksQueryReference}
          navigation={navigation}
          refreshHandler={refreshHandler}
          schoolId={schoolData.school.id}
          startingYears={startingYears}
          state={state}
        />
      ) : (
        <ListEmptyBanner
          explainer={"This hasn't been built yet..."}
          flexGrow={1}
          fontSize="3xl"
          py="4"
        >
          <Text fontSize="110">{"👷"}</Text>
        </ListEmptyBanner>
      )
    ) : (
      <ProblemSplash navigation={navigation} />
    );
  }, [
    dispatchState,
    loadBlacklistedDatesQueryReference,
    loadLessonBlocksQueryReference,
    loadTermDatesQueryReference,
    navigation,
    refreshHandler,
    schoolData.school,
    startingYears,
    state,
    termDatesAndBlacklistedDatesRefreshHandler,
  ]);

  return (
    <Box flex={1}>
      {renderSideBar}
      <Box
        flex={1}
        flexGrow={1}
        ml={state.values.sideBarIsCollapsed ? SIDE_BAR_COLLAPSED_WIDTH : SIDE_BAR_EXPANDED_WIDTH}
        mt="70"
        pt="6"
        px="6"
      >
        <TopTabBar
          bg="primary.100"
          borderRadius="lg"
          buttonStyle={{
            _hover: { bg: "primary.200" },
            _pressed: { bg: "primary.300" },
            alignItems: "center",
            bg: "primary.50",
            pb: 2,
            pt: 2,
          }}
          containerProps={{ pt: 4 }}
          currentPageIndex={state.values.currentPageIndex ?? 0}
          hideBottomBar
          mb="6"
          overflow="hidden"
          pages={["Lessons", "Term Dates", "Rates"]}
          selectedButtonStyle={{ bg: "primary.100" }}
          selectedTextStyle={{ color: "primary.600" }}
          setCurrentPageIndex={(index) => {
            dispatchState({
              input: "currentPageIndex",
              value: index,
            });
            navigation.setParams({ currentPageIndex: index });
          }}
          shadow={1}
          textStyle={{ color: "primary.800" }}
        />
        {renderTab}
      </Box>
      {renderModals}
    </Box>
  );
};

const SchoolScreen: FC<ScreenProps> = (props) => {
  const { navigation, route } = props;

  const [defaultStartingYear, startingYears] = useMemo(
    () => getDefaultStartingYearAndStartingYearsForSelect(),
    []
  );

  const { user } = useAuth();

  const initialState = useMemo(() => {
    let initialStartingYear = route.params?.startingYear;
    if (initialStartingYear === undefined) {
      initialStartingYear = user?.profile?.profileGroup?.currentStartingYear ?? defaultStartingYear;
      navigation.setParams({
        startingYear: initialStartingYear,
      });
    }
    let initialBlock = route.params?.block;
    if (initialBlock === undefined) {
      if (
        user?.profile?.profileGroup?.currentStartingYear &&
        user?.profile?.profileGroup?.currentStartingYear > defaultStartingYear
      ) {
        initialBlock = 1;
      } else {
        initialBlock = user?.profile?.currentBlock ?? getCurrentBlock();
      }
      navigation.setParams({
        block: initialBlock,
      });
    }
    return {
      values: {
        block: Number(initialBlock),
        contentIsRendered: false,
        currentPageIndex: Number(route.params?.currentPageIndex ?? 0),
        dayIndex: route.params?.dayIndex,
        isRefetching: false,
        orderBy: route.params?.orderBy ?? "lessonDay,lessonTime",
        searchTerm: route.params?.searchTerm ?? "",
        startingYear: Number(initialStartingYear),
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reducer = createReducer<ReducerValues>(initialState);
  const [state, dispatchState] = useReducer(reducer, initialState);

  const [loadSchoolQueryReference, loadSchoolQuery] = useQueryLoader<LoadSchoolQuery>(load_school);

  useEffect(() => {
    loadSchoolQuery(
      {
        schoolId: route.params.schoolId,
      },
      { fetchPolicy: "store-or-network" }
    );
  }, [loadSchoolQuery, route.params.schoolId]);

  const renderLoadingFallback = useCallback(() => {
    return (
      <HStack flex={1}>
        <SideBar
          isCollapsed={state.values.sideBarIsCollapsed}
          navigation={navigation}
          position="relative"
          setIsCollapsed={() =>
            dispatchState({
              input: "sideBarIsCollapsed",
              value: !state.values.sideBarIsCollapsed,
            })
          }
          top="0"
          width={
            state.values.sideBarIsCollapsed ? SIDE_BAR_COLLAPSED_WIDTH : SIDE_BAR_EXPANDED_WIDTH
          }
        />
        <LoadingBlobs
          ml={
            state.values.sideBarIsCollapsed
              ? SIDE_BAR_COLLAPSED_WIDTH + 1
              : SIDE_BAR_EXPANDED_WIDTH + 1
          }
          pt="70"
        >
          Loading School...
        </LoadingBlobs>
      </HStack>
    );
  }, [navigation, state.values.sideBarIsCollapsed]);

  return (
    <Box bg="surface.100" flex={1}>
      {loadSchoolQueryReference != null ? (
        <Suspense fallback={renderLoadingFallback()}>
          <SchoolContent
            dispatchState={dispatchState}
            loadSchoolQueryReference={loadSchoolQueryReference}
            navigation={navigation}
            route={route}
            schoolId={route.params.schoolId}
            startingYears={startingYears}
            state={state}
          />
        </Suspense>
      ) : (
        renderLoadingFallback()
      )}
    </Box>
  );
};

export const screenOptions = {
  headerTitle: "School",
};

export default SchoolScreen;
